/**
 * Lokaso — shared PWA install helper.
 *
 * Captures the `beforeinstallprompt` event ONCE at module load (before any
 * React component mounts) so multiple UI surfaces (bottom banner, /app landing
 * page, header CTA) can share the same deferred prompt.
 *
 * Also exposes `isStandalone`, `isIos`, `isSafari`, `isAndroid`, `isChromeIos`
 * and an install() helper that fires the native prompt when available.
 */

let _deferredPrompt = null;
let _installed = isStandaloneImmediate();
const _listeners = new Set();

function isStandaloneImmediate() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function notify() {
  for (const cb of _listeners) {
    try { cb({ deferredPrompt: _deferredPrompt, installed: _installed }); }
    // eslint-disable-next-line no-console
    catch (e) { console.error("[pwa] listener error:", e); }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Chrome/Edge/Android — save the deferred event so we can trigger it later.
    e.preventDefault();
    _deferredPrompt = e;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    _installed = true;
    notify();
  });
}

export function getDeferredPrompt() { return _deferredPrompt; }
export function isPwaInstalled() { return _installed || isStandaloneImmediate(); }

/** Subscribe to install-state changes. Returns unsubscribe fn. */
export function subscribePwa(cb) {
  _listeners.add(cb);
  // Fire immediately so subscribers get current state
  try { cb({ deferredPrompt: _deferredPrompt, installed: _installed }); }
  // eslint-disable-next-line no-console
  catch (e) { console.error("[pwa] listener error:", e); }
  return () => _listeners.delete(cb);
}

/** Trigger the native install prompt. Resolves to the userChoice outcome
 *  ('accepted' | 'dismissed') or null if unavailable. */
export async function triggerInstallPrompt() {
  if (!_deferredPrompt) return null;
  try {
    _deferredPrompt.prompt();
    const choice = await _deferredPrompt.userChoice;
    _deferredPrompt = null;
    notify();
    return choice?.outcome || null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[pwa] prompt failed:", e);
    return null;
  }
}

/** UA-based device detection helpers. */
export function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

export function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export function isSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Safari on iOS or macOS — exclude Chrome/CriOS/FxiOS/EdgiOS/Android
  return /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
}

export function isChromeOnIos() {
  if (typeof navigator === "undefined") return false;
  return /CriOS|FxiOS|EdgiOS/i.test(navigator.userAgent);
}
